import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useFieldArray } from 'react-hook-form';
import { workflowApi, WorkflowStep } from '../../services/api';
import { Wizard } from '../Wizard';
import type { WizardStep } from '../Wizard';
import {
  channelTemplates,
  workflowBasicsSchema,
  workflowBuilderDefaults,
  workflowChannelSchema,
  workflowReviewSchema,
  workflowStepsSchema,
  type WorkflowBuilderValues,
} from './workflowBuilder.schema';

function StepsEditor({ form }: { form: import('react-hook-form').UseFormReturn<WorkflowBuilderValues> }) {
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'steps' });
  const channel = form.watch('channel');

  const applyChannelTemplate = () => {
    const template = channelTemplates[channel] ?? [];
    form.setValue('steps', template.map((t, idx) => ({
      id: `step-${Date.now()}-${idx}`,
      ...t,
      systemPrompt: '',
    })));
  };

  return (
    <div className="form-section">
      <div className="page-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Schritte</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {channel !== 'custom' && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={applyChannelTemplate}>
              Template übernehmen
            </button>
          )}
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={() => append({
              id: `step-${Date.now()}`,
              name: `Schritt ${fields.length + 1}`,
              provider: 'openai',
              prompt: '',
              systemPrompt: '',
            })}
          >
            Schritt hinzufügen
          </button>
        </div>
      </div>
      {fields.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', marginTop: 12 }}>
          Noch keine Schritte. Füge Schritte hinzu oder nutze ein Template für den gewählten Kanal.
        </p>
      )}
      {fields.map((field, index) => (
        <div key={field.id} className="step-card">
          <div className="step-card-header">
            <span className="step-number">Schritt {index + 1}</span>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => remove(index)}>Entfernen</button>
          </div>
          <div className="form-group">
            <label className="form-label">Name</label>
            <Controller
              name={`steps.${index}.name`}
              control={form.control}
              render={({ field: f }) => <input className="form-input" type="text" {...f} />}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Anbieter</label>
            <Controller
              name={`steps.${index}.provider`}
              control={form.control}
              render={({ field: f }) => (
                <select className="form-select" {...f}>
                  <option value="openai">OpenAI</option>
                  <option value="claude">Claude</option>
                  <option value="perplexity">Perplexity</option>
                </select>
              )}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Prompt (verwende {'{{'} variable {'}}'}&nbsp; für Platzhalter)</label>
            <Controller
              name={`steps.${index}.prompt`}
              control={form.control}
              render={({ field: f }) => (
                <textarea className="form-textarea" rows={4} placeholder="z. B. Erstelle einen Beitrag über {{thema}}" {...f} />
              )}
            />
          </div>
          <div className="form-group">
            <label className="form-label">System-Prompt (optional)</label>
            <Controller
              name={`steps.${index}.systemPrompt`}
              control={form.control}
              render={({ field: f }) => (
                <textarea className="form-textarea" rows={2} {...f} value={f.value ?? ''} />
              )}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WorkflowBuilderWizard() {
  const navigate = useNavigate();

  const steps: readonly WizardStep<WorkflowBuilderValues>[] = [
    {
      id: 'basics',
      label: 'Basis',
      description: 'Name und Beschreibung des Workflows',
      schema: workflowBasicsSchema,
      fields: ['name', 'description'],
      render: (form) => (
        <>
          <div className="form-group">
            <label className="form-label">Workflow-Name</label>
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <input className="form-input" type="text" {...field} required />
                  {fieldState.error && <small style={{ color: 'var(--color-error)' }}>{fieldState.error.message}</small>}
                </>
              )}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Beschreibung</label>
            <Controller
              name="description"
              control={form.control}
              render={({ field }) => (
                <textarea className="form-textarea" rows={3} {...field} value={field.value ?? ''} />
              )}
            />
          </div>
        </>
      ),
    },
    {
      id: 'channel',
      label: 'Kanal',
      description: 'Wofür ist dieser Workflow gedacht?',
      schema: workflowChannelSchema,
      fields: ['channel'],
      render: (form) => (
        <div className="form-group">
          <label className="form-label">Kanal</label>
          <Controller
            name="channel"
            control={form.control}
            render={({ field }) => (
              <select className="form-select" {...field}>
                <option value="custom">Eigener Workflow</option>
                <option value="linkedin">LinkedIn-Beitrag</option>
                <option value="email">E-Mail-Kampagne</option>
                <option value="blog">Blog-Artikel</option>
              </select>
            )}
          />
          <small style={{ color: 'var(--color-text-muted)' }}>
            Bei Auswahl eines Kanals kannst du im nächsten Schritt ein passendes Template übernehmen.
          </small>
        </div>
      ),
    },
    {
      id: 'steps',
      label: 'Schritte',
      description: 'Definiere die Schritte deines Workflows',
      schema: workflowStepsSchema,
      fields: ['steps'],
      render: (form) => <StepsEditor form={form} />,
    },
    {
      id: 'review',
      label: 'Überblick',
      description: 'Alles bereit zum Anlegen?',
      schema: workflowReviewSchema,
      fields: [],
      render: (form) => {
        const values = form.getValues();
        return (
          <div>
            <p><strong>Name:</strong> {values.name}</p>
            {values.description && <p><strong>Beschreibung:</strong> {values.description}</p>}
            <p><strong>Kanal:</strong> {values.channel}</p>
            <p><strong>Schritte:</strong> {values.steps.length}</p>
            <ol style={{ paddingLeft: 20 }}>
              {values.steps.map((s, i) => (
                <li key={s.id}>{s.name} <em style={{ color: 'var(--color-text-muted)' }}>({s.provider})</em></li>
              ))}
            </ol>
          </div>
        );
      },
    },
  ];

  const handleFinish = async (values: WorkflowBuilderValues) => {
    const payload = {
      name: values.name,
      description: values.description,
      steps: values.steps.map((s): WorkflowStep => ({
        id: s.id,
        name: s.name,
        provider: s.provider,
        prompt: s.prompt,
        systemPrompt: s.systemPrompt,
      })),
    };
    await workflowApi.createWorkflow(payload);
    navigate('/workflows');
  };

  return (
    <Wizard<WorkflowBuilderValues>
      steps={steps}
      defaultValues={workflowBuilderDefaults}
      onFinish={handleFinish}
      onCancel={() => navigate('/workflows')}
      submitLabel="Workflow anlegen"
      title="Workflow-Builder"
      persistKey="wizard:intermal:workflow-builder"
    />
  );
}
